import { Auth0Provider } from '@auth0/auth0-react';
import { ClerkProvider, SignIn, Show, useOrganizationList, useUser } from '@clerk/react';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import 'react-tooltip/dist/react-tooltip.css';
import Authenticated from './Authenticated';
import { AuthenticationErrorsWrapper } from './AuthenticationErrorsWrapper';
import Main from './Main';
import SubscriptionWrapper from './Subscription/SubscriptionWrapper';
import useSubscription from './Subscription/useSubscription';
import { TEMP_DIRECT_LINK, DIRECT_LINK } from '../Shared/constants';
import { AuthProvider } from './Types/AuthProviders';
import { hasLegacyAuth0Session } from './Subscription/legacyAuth0Session';

function ClerkSignedInContent({ storedOrg }): React.ReactElement {
    const { user } = useUser();
    const { setActive } = useOrganizationList();
    const didInitRef = React.useRef(false);

    useEffect(() => {
        if (didInitRef.current) return;
        if (!user || !storedOrg) return;

        didInitRef.current = true;
        user.update({ unsafeMetadata: { idplans_company_id: storedOrg } });
        setActive({ organization: storedOrg });
    }, [user, storedOrg, setActive]);

    return <Main />;
}

function AuthenticatedRoot(): React.ReactElement | null {
    const history = useHistory();
    const {
        authProvider,
        email,
        organization: storedOrg,
        logoutUser,
        logoutAuth0User,
        clearUser,
        invitation,
        subscription,
    } = useSubscription();
    // Keep blocking the app after cleanup until the logout navigation completes.
    const [isLegacyAuth0Context] = React.useState(() => hasLegacyAuth0Session(subscription.clientId));
    const legacyLogoutStarted = React.useRef(false);
    const isClerkInvite = window.location.pathname.includes('__clerk_db_jwt');

    useEffect(() => {
        if (isLegacyAuth0Context) {
            if (!legacyLogoutStarted.current) {
                legacyLogoutStarted.current = true;
                clearUser();
                logoutAuth0User('login');
            }
            return;
        }

        if (!invitation && !storedOrg) {
            clearUser();
            logoutUser('login');
        }
    }, [isLegacyAuth0Context, invitation, storedOrg, clearUser, logoutUser, logoutAuth0User]);

    const onRedirectCallback = React.useCallback(
        (appState) => {
            const directLink = sessionStorage.getItem(DIRECT_LINK);
            sessionStorage.removeItem(TEMP_DIRECT_LINK);
            sessionStorage.removeItem(DIRECT_LINK);
            history.push(directLink || appState?.returnTo || window.location.pathname);
        },
        [history],
    );

    // Do not mount either provider while a legacy session is being logged out.
    if (isLegacyAuth0Context) return null;

    if (!isClerkInvite && (storedOrg === undefined || storedOrg === null)) {
        // bounch user to login page if no stored org is found
        clearUser();
        logoutUser('login');
        return null;
    }

    return (
        <AuthenticationErrorsWrapper>
            <SubscriptionWrapper>
                {({ domain, clientId, redirectUri, organization, audience }) =>
                    authProvider === AuthProvider.Clerk ? (
                        <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
                            <Show when="signed-out">
                                <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
                                    <SignIn
                                        appearance={{
                                            elements: {
                                                footer: {
                                                    display: 'none',
                                                },

                                                identityPreview: {
                                                    display: 'none',
                                                },
                                            },
                                        }}
                                        initialValues={{ emailAddress: email }}
                                    />
                                </div>
                            </Show>
                            <Show when="signed-in">
                                <ClerkSignedInContent storedOrg={storedOrg} />
                            </Show>
                        </ClerkProvider>
                    ) : (
                        <Auth0Provider
                            audience={audience}
                            clientId={clientId}
                            organization={organization}
                            domain={domain}
                            invitation={invitation}
                            redirectUri={redirectUri}
                            useRefreshTokens
                            login_hint={email}
                            onRedirectCallback={onRedirectCallback}
                        >
                            <Authenticated>
                                <Main />
                            </Authenticated>
                        </Auth0Provider>
                    )
                }
            </SubscriptionWrapper>
        </AuthenticationErrorsWrapper>
    );
}

export default AuthenticatedRoot;
